import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent, CircularProgress } from '@mui/material';
import { Group, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { groupService } from '../services/api';

const JoinGroup: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [info, setInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [groupName, setGroupName] = useState('');

  useEffect(() => {
    if (!token) return;
    groupService.getJoinInfo(token)
      .then(data => { setInfo(data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.error || 'Invalid or expired invite link'); setLoading(false); });
  }, [token]);

  const handleJoin = async () => {
    if (!token) return;
    setJoining(true);
    try {
      const result = await groupService.joinGroup(token);
      setGroupName(result.group_name);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to join group');
    } finally {
      setJoining(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', p: 3 }}>
      <Card sx={{ maxWidth: 420, width: '100%', background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)', border: '1px solid rgba(0,212,255,0.2)' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {loading ? (
            <CircularProgress sx={{ color: '#00d4ff' }} />
          ) : error && !success ? (
            <>
              <ErrorIcon sx={{ fontSize: 48, color: '#f44336', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#f44336', mb: 2 }}>{error}</Typography>
              <Button variant="outlined" onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
            </>
          ) : success ? (
            <>
              <CheckCircle sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#4caf50', mb: 1 }}>Joined!</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 3 }}>
                You are now a member of <strong>{groupName || info?.group_name}</strong>.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/dashboard')} sx={{ background: 'linear-gradient(135deg, #00d4ff, #0099cc)' }}>
                Go to Dashboard
              </Button>
            </>
          ) : (
            <>
              <Group sx={{ fontSize: 48, color: '#00d4ff', mb: 2 }} />
              <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>
                Group Invite
              </Typography>
              <Card sx={{ mb: 3, background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="h6" sx={{ color: '#00d4ff', fontWeight: 700 }}>
                    {info?.group_name}
                  </Typography>
                  {info?.description && (
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1 }}>
                      {info.description}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1 }}>
                    Created by {info?.owner_name}
                  </Typography>
                </CardContent>
              </Card>
              <Button
                variant="contained" fullWidth onClick={handleJoin} disabled={joining}
                startIcon={joining ? <CircularProgress size={18} /> : <CheckCircle />}
                sx={{ py: 1.5, background: 'linear-gradient(135deg, #4caf50, #2e7d32)', fontSize: '1rem', fontWeight: 700 }}
              >
                {joining ? 'Joining...' : 'Join Group'}
              </Button>
              <Button variant="text" onClick={() => navigate('/dashboard')} sx={{ mt: 1, color: 'rgba(255,255,255,0.3)' }}>
                Decline
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default JoinGroup;
